import { useId, type ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Button } from '../ui/button/Button';
import { useDismissablePopover } from './useDismissablePopover';

export interface FilterPopoverProps {
    /** Number of filters currently set; shown on the trigger and drives its colour. */
    activeCount: number;
    onClearAll: () => void;
    /** One element per filter (e.g. a `div` holding a `Label` and its control). Changes apply as made. */
    children: ReactNode;
    /** `two` lays the fields out in two columns on wider screens, for longer filter sets. */
    columns?: 'one' | 'two';
    label?: string;
}

const PANEL_CLASSES =
    'z-30 rounded-lg border border-mp-border bg-mp-surface p-4 shadow-lg max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:rounded-b-none sm:absolute sm:right-0 sm:top-full sm:mt-2';

const WIDTH_CLASSES = { one: 'sm:w-80', two: 'sm:w-xl' };
const GRID_CLASSES = { one: 'grid gap-4', two: 'grid gap-4 sm:grid-cols-2' };

export function FilterPopover({ activeCount, onClearAll, children, columns = 'one', label = 'Filters' }: FilterPopoverProps) {
    const panelId = useId();
    const popover = useDismissablePopover();
    const hasActive = activeCount > 0;

    return (
        <div className="relative" {...popover.containerProps}>
            <Button
                ref={popover.triggerRef}
                type="button"
                size="sm"
                variant={hasActive ? 'primary' : 'neutral'}
                appearance={popover.isOpen ? 'solid' : 'outline'}
                startIcon={<SlidersHorizontal aria-hidden="true" className="size-4" />}
                ariaExpanded={popover.isOpen}
                ariaControls={panelId}
                onClick={popover.toggle}
            >
                {hasActive ? `${label} (${activeCount})` : label}
            </Button>
            {popover.isOpen && (
                <div
                    ref={popover.panelRef}
                    id={panelId}
                    role="dialog"
                    aria-label={label}
                    className={`${PANEL_CLASSES} ${WIDTH_CLASSES[columns]}`}
                >
                    <div className={GRID_CLASSES[columns]}>{children}</div>
                    <div className="mt-4 flex items-center justify-between border-t border-mp-border pt-3">
                        <Button
                            type="button"
                            size="xs"
                            variant="neutral"
                            appearance="ghost"
                            disabled={!hasActive}
                            onClick={onClearAll}
                        >
                            Clear all
                        </Button>
                        <Button type="button" size="xs" onClick={() => popover.close()}>
                            Done
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
